import { createTheme } from "@mui/material/styles";
import type * as React from "react";
import { tokens } from "./tokens";
// Enables `components.MuiDataGrid` in the theme, so the grid can be styled
// centrally instead of hand-wrapped at its call site.
import type {} from "@mui/x-data-grid/themeAugmentation";

// Define custom breakpoints
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    laptop: true;
    tablet: true;
    mobile: true;
    desktop: true;
    ultraLarge: true;
  }
}

// The small uppercase amber label the site uses above headings.
declare module "@mui/material/styles" {
  interface TypographyVariants {
    eyebrow: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    eyebrow?: React.CSSProperties;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    eyebrow: true;
  }
}

const BREAKPOINTS = {
  // `mobile` must stay 0 — it is the base for every `up()` query.
  mobile: 0,
  tablet: 640,
  laptop: 1024,
  desktop: 1280,
  ultraLarge: 1920,
};

/**
 * Breakpoint helpers only, used to build media-query keys below.
 *
 * Always go through `breakpoints.down()/up()` rather than interpolating
 * `breakpoints.values` into a template string: `mobile` is 0, so a hand-written
 * `max-width: ${values.mobile}px` compiles to `max-width: 0px` and never matches.
 */
const bp = createTheme({ breakpoints: { values: BREAKPOINTS } }).breakpoints;

/**
 * The theme is built in ONE pass on purpose. Passing `typography` as a second
 * `createTheme(base, options)` argument only deep-merges over variants that MUI
 * has already generated with its default Roboto stack, so a top-level
 * `fontFamily` never reaches h1..caption and every heading silently renders in
 * Roboto.
 *
 * Note: do not reach for `responsiveFontSizes()` — it hardcodes `sm`/`md`/`lg`,
 * which `BreakpointOverrides` above disables.
 */
const theme = createTheme({
  breakpoints: { values: BREAKPOINTS },
  shape: { borderRadius: tokens.radius.base },
  palette: {
    mode: "light",
    primary: { main: tokens.color.amber, contrastText: tokens.color.ink },
    // Flipped from #FFFFFF to ink. `color="secondary"` is used across the app to
    // mean "the readable text colour", so this one value corrects ~31 call sites.
    secondary: { main: tokens.color.ink, contrastText: tokens.color.surface },
    text: {
      primary: tokens.color.ink,
      secondary: tokens.color.muted,
      disabled: tokens.color.disabled,
    },
    background: {
      default: tokens.color.pageBg,
      paper: tokens.color.surface,
    },
    divider: tokens.color.hairline,
    action: {
      disabled: tokens.color.disabled,
      disabledBackground: tokens.color.hairline,
    },
  },
  typography: {
    fontFamily: tokens.font.family,
    h1: {
      fontWeight: 800,
      fontSize: "56px",
      lineHeight: 1.05,
      letterSpacing: "-1.5px",
      color: tokens.color.ink,
      [bp.down("tablet")]: {
        fontSize: "36px",
        letterSpacing: "-0.8px",
      },
    },
    h2: {
      fontWeight: 800,
      fontSize: "40px",
      lineHeight: 1.1,
      letterSpacing: "-0.8px",
      color: tokens.color.ink,
      [bp.down("tablet")]: {
        fontSize: "30px",
        letterSpacing: "-0.5px",
      },
    },
    h3: {
      fontWeight: 800,
      fontSize: "28px",
      lineHeight: 1.3,
      letterSpacing: "-0.4px",
      color: tokens.color.ink,
    },
    h4: {
      fontWeight: 800,
      fontSize: "22px",
      lineHeight: 1.3,
      letterSpacing: "-0.22px",
      color: tokens.color.ink,
    },
    h5: {
      fontWeight: 800,
      fontSize: "20px",
      lineHeight: 1.35,
      letterSpacing: "-0.2px",
      color: tokens.color.ink,
    },
    h6: {
      fontWeight: 700,
      fontSize: "18px",
      lineHeight: 1.4,
      color: tokens.color.ink,
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: "18px",
      lineHeight: 1.5,
      color: tokens.color.body,
    },
    subtitle2: {
      fontWeight: 700,
      fontSize: "16px",
      lineHeight: 1.5,
      color: tokens.color.ink,
    },
    body1: {
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      color: tokens.color.body,
    },
    body2: {
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "22px",
      color: tokens.color.body,
    },
    caption: {
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: 1.5,
      color: tokens.color.muted,
    },
    button: {
      fontWeight: 700,
      fontSize: "15px",
      lineHeight: 1,
      textTransform: "none",
      letterSpacing: 0,
    },
    eyebrow: {
      fontWeight: 700,
      fontSize: "12px",
      lineHeight: 1.4,
      letterSpacing: "1.7px",
      textTransform: "uppercase",
      color: tokens.color.amber,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: { eyebrow: "p" },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        "*": { boxSizing: "border-box" },
        "html, body": {
          margin: 0,
          height: "100%",
          fontFamily: tokens.font.family,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        body: {
          backgroundColor: tokens.color.pageBg,
          color: tokens.color.body,
        },
        ":root": { colorScheme: "light" },
        a: { color: "inherit", textDecorationColor: tokens.color.hairline },
      },
    },

    // Without this, MUI defaults `maxWidth` to `lg` — a key that
    // BreakpointOverrides disables — producing `max-width: undefinedpx`, which
    // the browser drops. `desktop` is 1280, matching the site's container.
    MuiContainer: {
      defaultProps: { maxWidth: "desktop" },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.pill,
          textTransform: "none",
          boxShadow: "none",
          transition: "all 0.3s",
          "&:hover": { boxShadow: "none" },
          "&.Mui-disabled": {
            backgroundColor: tokens.color.hairline,
            color: tokens.color.disabled,
          },
        },
        // All three sizes are pinned: many existing call sites pass
        // size="large", and they should land on the site's spec unchanged.
        sizeSmall: { padding: "10px 20px", fontSize: 14 },
        sizeMedium: { padding: "15px 28px", fontSize: 15 },
        sizeLarge: { padding: "15px 28px", fontSize: 15 },
        containedPrimary: {
          backgroundColor: tokens.color.amber,
          color: tokens.color.ink,
          "&:hover": { backgroundColor: tokens.color.amberHover },
        },
        containedSecondary: {
          backgroundColor: tokens.color.ink,
          color: tokens.color.surface,
          "&:hover": { backgroundColor: "#333333" },
        },
        outlined: {
          backgroundColor: tokens.color.surface,
          borderColor: tokens.color.hairline,
          color: tokens.color.ink,
          "&:hover": {
            backgroundColor: tokens.color.surface,
            borderColor: tokens.color.ink,
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: tokens.radius.card },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.surface,
          border: `1px solid ${tokens.color.hairline}`,
          borderRadius: tokens.radius.card,
          boxShadow: "none",
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: tokens.space.cardPadding,
          "&:last-child": { paddingBottom: tokens.space.cardPadding },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.card,
          backgroundColor: tokens.color.surface,
          border: `1px solid ${tokens.color.hairline}`,
          boxShadow: tokens.shadow.md,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 22,
          fontWeight: 800,
          letterSpacing: "-0.22px",
          color: tokens.color.ink,
          padding: "24px 24px 8px",
        },
      },
    },

    MuiDialogContentText: {
      styleOverrides: {
        root: { color: tokens.color.body },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.input,
          backgroundColor: tokens.color.surface,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.color.hairline,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.color.disabled,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.color.amber,
            borderWidth: 1,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: tokens.color.muted,
          "&.Mui-focused": { color: tokens.color.ink },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: { color: tokens.color.muted },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.color.hairline },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: tokens.color.amber, height: 3 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          fontSize: 15,
          color: tokens.color.muted,
          "&.Mui-selected": { color: tokens.color.ink },
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${tokens.color.hairline}`,
          borderRadius: tokens.radius.card,
          backgroundColor: tokens.color.surface,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: tokens.color.pageBg,
            color: tokens.color.ink,
            fontWeight: 700,
            borderBottom: `1px solid ${tokens.color.hairline}`,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${tokens.color.hairline}`,
            color: tokens.color.body,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: tokens.color.pageBg,
          },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
        },
      },
    },
  },
});

export default theme;
